import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, DatePicker, Select, Checkbox, Col, Row, Divider, message, FormInstance } from 'antd';
import moment from 'moment';
import { Decedent, DecedentPayload, Estate } from '../../types';
import { US_STATES } from './usstates';
import { addDecedent, editDecedent } from './estateService';

const { Option } = Select;

/**
 * The decedent form lives under a `decedent` key so the same fields can sit
 * inside the create-estate form and inside the standalone add/edit modal
 * without being written twice.
 */
const NAME = 'decedent';

const path = (...rest: string[]) => [NAME, ...rest];

const addressFields = (group: 'mailingAddress' | 'physicalAddress', disabled = false) => (
  <>
    <Form.Item label="Address Line 1" name={path(group, 'addressLine1')}>
      <Input placeholder="Enter street address" disabled={disabled} />
    </Form.Item>
    <Form.Item label="Address Line 2" name={path(group, 'addressLine2')}>
      <Input placeholder="Apartment, suite, etc. (optional)" disabled={disabled} />
    </Form.Item>
    <Row gutter={16}>
      <Col span={8}>
        <Form.Item label="City" name={path(group, 'city')}>
          <Input placeholder="Enter city" disabled={disabled} />
        </Form.Item>
      </Col>
      <Col span={8}>
        <Form.Item label="County" name={path(group, 'county')}>
          <Input placeholder="Enter county" disabled={disabled} />
        </Form.Item>
      </Col>
      <Col span={8}>
        <Form.Item label="Zip Code" name={path(group, 'zipCode')}>
          <Input placeholder="Enter zip code" disabled={disabled} />
        </Form.Item>
      </Col>
    </Row>
    <Form.Item label="State" name={path(group, 'state')}>
      <Select
        placeholder="Select state"
        disabled={disabled}
        showSearch
        optionFilterProp="children"
        allowClear
      >
        {US_STATES.map((state) => (
          <Option key={state.code} value={state.code}>
            {state.name}
          </Option>
        ))}
      </Select>
    </Form.Item>
  </>
);

/** True where any value has been entered anywhere in a nested object. */
const hasAnyValue = (value: any): boolean => {
  if (value === undefined || value === null || value === '' || value === false) return false;
  if (Array.isArray(value)) return value.some(hasAnyValue);
  if (typeof value === 'object' && !moment.isMoment(value)) {
    return Object.values(value).some(hasAnyValue);
  }
  return true;
};

interface DecedentFieldsProps {
  form: FormInstance;
  /**
   * Set where the decedent as a whole may be left out, as on the create-estate
   * form. The name is still required once anything else has been filled in:
   * an address with nobody attached to it cannot be saved.
   */
  optional?: boolean;
}

/**
 * Every field describing the person who died, for embedding in a wider form.
 */
export const DecedentFields: React.FC<DecedentFieldsProps> = ({ form, optional = false }) => {
  const decedent = Form.useWatch(NAME, form);
  const physicalDiffers = decedent?.physicalDiffers;
  const nameRequired = !optional || hasAnyValue(decedent);

  return (
    <>
      <Row gutter={16}>
        <Col span={6}>
          <Form.Item label="Prefix" name={path('prefix')}>
            <Input placeholder="e.g., Mr., Ms." />
          </Form.Item>
        </Col>
        <Col span={9}>
          <Form.Item
            label="First Name"
            name={path('firstName')}
            rules={[{ required: nameRequired, message: "Please enter the decedent's first name!" }]}
          >
            <Input placeholder="Enter first name" />
          </Form.Item>
        </Col>
        <Col span={9}>
          <Form.Item label="Middle Name" name={path('middleName')}>
            <Input placeholder="Enter middle name (optional)" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Last Name"
            name={path('lastName')}
            rules={[{ required: nameRequired, message: "Please enter the decedent's last name!" }]}
          >
            <Input placeholder="Enter last name" />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label="Suffix" name={path('suffix')}>
            <Input placeholder="e.g., Jr., Sr." />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label="Sex" name={path('sex')}>
            <Select placeholder="Select sex" allowClear>
              <Option value="male">Male</Option>
              <Option value="female">Female</Option>
              <Option value="other">Other</Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item label="Date of Birth" name={path('dob')}>
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" placeholder="Select date" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label="Date of Death" name={path('dateOfDeath')}>
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" placeholder="Select date" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label="Marital Status" name={path('maritalStatus')}>
            <Select placeholder="Select status" allowClear>
              <Option value="single">Single</Option>
              <Option value="married">Married</Option>
              <Option value="widowed">Widowed</Option>
              <Option value="divorced">Divorced</Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label="Place of Death" name={path('placeOfDeathName')}>
            <Input placeholder="e.g., name of hospital or facility" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Place of Death Address" name={path('placeOfDeathAddress')}>
            <Input placeholder="Enter address" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label="Citizenship" name={path('citizenship')}>
            <Input placeholder="e.g., US" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label=" " colon={false} name={path('willExists')} valuePropName="checked">
            <Checkbox>A will exists</Checkbox>
          </Form.Item>
        </Col>
      </Row>

      <Divider orientation="left" plain>
        Mailing Address
      </Divider>
      {addressFields('mailingAddress')}

      <Form.Item name={path('physicalDiffers')} valuePropName="checked">
        <Checkbox>Physical address is different from the mailing address</Checkbox>
      </Form.Item>
      {physicalDiffers && (
        <>
          <Divider orientation="left" plain>
            Physical Address
          </Divider>
          {addressFields('physicalAddress')}
        </>
      )}
    </>
  );
};

const addressPayload = (values: any) => ({
  addressLine1: values?.addressLine1 || '',
  addressLine2: values?.addressLine2 || '',
  city: values?.city || '',
  county: values?.county || '',
  state: values?.state || '',
  zipCode: values?.zipCode || '',
});

/**
 * Turns the nested form values into the request body.
 *
 * The physical address is sent only when it differs. Left out, the server
 * treats the mailing address as the physical one too, which is what the
 * "if different" wording promises.
 */
export const decedentPayloadFrom = (values: any): DecedentPayload => {
  const decedent = values?.[NAME] ?? {};

  return {
    prefix: decedent.prefix || '',
    firstName: decedent.firstName,
    middleName: decedent.middleName || '',
    lastName: decedent.lastName,
    suffix: decedent.suffix || '',
    sex: decedent.sex || '',
    dob: decedent.dob ? moment(decedent.dob).format('YYYY-MM-DD') : undefined,
    dateOfDeath: decedent.dateOfDeath
      ? moment(decedent.dateOfDeath).format('YYYY-MM-DD')
      : undefined,
    placeOfDeathName: decedent.placeOfDeathName || '',
    placeOfDeathAddress: decedent.placeOfDeathAddress || '',
    citizenship: decedent.citizenship || '',
    willExists: decedent.willExists || false,
    maritalStatus: decedent.maritalStatus || '',
    mailingAddress: addressPayload(decedent.mailingAddress),
    ...(decedent.physicalDiffers
      ? { physicalAddress: addressPayload(decedent.physicalAddress) }
      : {}),
  };
};

const addressOfType = (decedent: Decedent | null | undefined, addressType: string) =>
  decedent?.addresses?.find((a) => a.addressType?.toLowerCase() === addressType);

/**
 * Whether a form has anything worth submitting under the decedent key.
 *
 * Matches what makes the name required, so a decedent is never dropped for
 * having been filled in somewhere other than the name fields.
 */
export const hasDecedentInput = (values: any): boolean => hasAnyValue(values?.[NAME]);

/**
 * Form values for an existing decedent.
 *
 * Mailing and physical are stored as separate rows even when they match, so
 * "differs" is decided by comparing them rather than by a stored flag.
 */
export const decedentFormValues = (decedent: Decedent | null | undefined) => {
  const mailing = addressOfType(decedent, 'mailing');
  const physical = addressOfType(decedent, 'domicile');

  const sameAddress =
    !physical ||
    !mailing ||
    ['addressLine1', 'addressLine2', 'city', 'county', 'state', 'zipCode'].every(
      (key) => (mailing as any)[key] === (physical as any)[key],
    );

  return {
    [NAME]: {
      prefix: decedent?.prefix || '',
      firstName: decedent?.firstName || '',
      middleName: decedent?.middleName || '',
      lastName: decedent?.lastName || '',
      suffix: decedent?.suffix || '',
      sex: decedent?.sex || undefined,
      dob: decedent?.dob ? moment(decedent.dob) : null,
      dateOfDeath: decedent?.dateOfDeath ? moment(decedent.dateOfDeath) : null,
      maritalStatus: decedent?.maritalStatus || undefined,
      placeOfDeathName: decedent?.placeOfDeathName || '',
      placeOfDeathAddress: decedent?.placeOfDeathAddress || '',
      citizenship: decedent?.citizenship || '',
      willExists: decedent?.willExists || false,
      mailingAddress: {
        addressLine1: mailing?.addressLine1 || '',
        addressLine2: mailing?.addressLine2 || '',
        city: mailing?.city || '',
        county: mailing?.county || '',
        state: mailing?.state || undefined,
        zipCode: mailing?.zipCode || '',
      },
      physicalDiffers: !sameAddress,
      physicalAddress: {
        addressLine1: physical?.addressLine1 || '',
        addressLine2: physical?.addressLine2 || '',
        city: physical?.city || '',
        county: physical?.county || '',
        state: physical?.state || undefined,
        zipCode: physical?.zipCode || '',
      },
    },
  };
};

interface DecedentModalProps {
  open: boolean;
  estateId: string;
  decedent?: Decedent | null;
  onCancel: () => void;
  onSubmit: (estate: Estate) => void;
}

/**
 * Adds the decedent to an estate that has none, or edits the one it has.
 */
const DecedentModal: React.FC<DecedentModalProps> = ({
  open,
  estateId,
  decedent,
  onCancel,
  onSubmit,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const isEdit = Boolean(decedent);

  useEffect(() => {
    if (!open) return;

    if (decedent) {
      form.setFieldsValue(decedentFormValues(decedent));
    } else {
      form.resetFields();
    }
  }, [open, decedent, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const payload = decedentPayloadFrom(values);
      const estate = isEdit
        ? await editDecedent(estateId, payload)
        : await addDecedent(estateId, payload);

      onSubmit(estate);
      form.resetFields();
      message.success(isEdit ? 'Decedent updated successfully' : 'Decedent added successfully');
      onCancel();
    } catch (error: any) {
      if (error?.errorFields) return; // validation, already shown on the fields
      console.error(error);
      message.error(error.message || (isEdit ? 'Failed to update decedent.' : 'Failed to add decedent.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={isEdit ? 'Edit Decedent' : 'Add Decedent'}
      open={open}
      onOk={handleOk}
      onCancel={onCancel}
      okText={isEdit ? 'Save' : 'Add'}
      cancelText="Cancel"
      confirmLoading={loading}
      width={720}
    >
      <Form form={form} layout="vertical">
        <DecedentFields form={form} />
      </Form>
    </Modal>
  );
};

export default DecedentModal;
